import { inject, Injectable } from '@angular/core';
import { AppConstants } from '@app/app.constants';
import { BehaviorSubject } from 'rxjs';

import { StorageService } from '../storage/storage.service';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class PwaInstallationService {
  private readonly storageService: StorageService = inject(StorageService);
  // Observable to notify when installation becomes available
  public installationAvailable$ = new BehaviorSubject<boolean>(false);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private isInstallationAvailable = false;

  constructor() {
    this.initBeforeInstallPrompt();
  }

  /**
   * Check if the app is running in standalone mode (already installed)
   */
  public isRunningInStandaloneMode(): boolean {
    // Modern browsers - check display-mode media query
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // iOS Safari fallback - navigator.standalone is iOS specific
    if ((navigator as any).standalone === true) {
      return true;
    }

    return false;
  }

  /**
   * Check if Chrome installation is available
   */
  public isChromeInstallationAvailable(): boolean {
    return this.isInstallationAvailable && this.deferredPrompt !== null;
  }

  /**
   * Check if iOS Safari installation should be shown
   */
  public async shouldShowIosInstallation(): Promise<boolean> {
    const userHasSkipped = await this.storageService.get(AppConstants.SKIP_PWA_INSTALLER);
    const isMobile = window.innerWidth <= 768;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isStandalone = this.isRunningInStandaloneMode();

    return isMobile && isSafari && !userHasSkipped && !isStandalone;
  }

  /**
   * Check if Chrome installation should be shown
   */
  public async shouldShowChromeInstallation(): Promise<boolean> {
    const userHasSkipped = await this.storageService.get(AppConstants.SKIP_PWA_INSTALLER);
    const isMobile = window.innerWidth <= 768;
    const isChrome = /chrome/i.test(navigator.userAgent) && !/edge/i.test(navigator.userAgent);
    const isStandalone = this.isRunningInStandaloneMode();

    return isMobile && isChrome && this.isChromeInstallationAvailable() && !userHasSkipped && !isStandalone;
  }

  /**
   * Trigger Chrome PWA installation
   */
  public async triggerChromeInstallation(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }

    try {
      // Show the install prompt
      await this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const choiceResult = await this.deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        await this.storageService.set(AppConstants.PWA_INSTALLED, true);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error triggering Chrome installation:', error);
      return false;
    } finally {
      // Clear the deferred prompt
      this.deferredPrompt = null;
      this.isInstallationAvailable = false;
      this.installationAvailable$.next(false);
    }
  }

  /**
   * Mark installation as skipped
   */
  public async skipInstallation(): Promise<void> {
    await this.storageService.set(AppConstants.SKIP_PWA_INSTALLER, true);
  }

  /**
   * Reset installation state (for testing)
   */
  public async resetInstallationState(): Promise<void> {
    await this.storageService.remove(AppConstants.SKIP_PWA_INSTALLER);
    await this.storageService.remove(AppConstants.PWA_INSTALLED);
  }

  /**
   * Initialize the beforeinstallprompt event listener for Chrome
   */
  private initBeforeInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();

      // Store the event so it can be triggered later
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.isInstallationAvailable = true;
      this.installationAvailable$.next(true);
    });

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      this.isInstallationAvailable = false;
      this.deferredPrompt = null;
      this.installationAvailable$.next(false);
      this.storageService.set(AppConstants.PWA_INSTALLED, true);
    });
  }
}
